import { ApolloQueryResult, useMutation, useQuery } from "@apollo/client";
import { TextField } from "@material-ui/core";
import Modal from "@material-ui/core/Modal";
import { get } from "lodash";
import React from "react";

import { Button, Loading, SimpleDropdown, StyledGrid } from "components/simple";
import { GetUserAssessmentsNamesByCycle, SaveInternalNotes } from "graphql/admin/assessments";
import client from "graphql/client";
import { GetAdminId } from "graphql/user/user.gql";
import { useDispatch } from "hooks";
import { UserAssessmentName } from "types/assessments";
import { StyledModalPaper } from "./styled";

interface ModalProps {
  userAssessmentCycleId: string;
  userAssessmentId?: string;
  noteId?: string;
  noteText?: string;
  refetch?: () => Promise<ApolloQueryResult<any>>;
}

const NotesModal: React.FC<ModalProps> = ({
  refetch,
  userAssessmentCycleId,
  userAssessmentId,
  noteId,
  noteText
}: ModalProps) => {
  const dispatch = useDispatch();
  const [notesValue, setNotesValue] = React.useState<string>("");
  const [userAssessmentValue, setUserAssessmentValue] = React.useState<string>(
    userAssessmentId || ""
  );
  const [createInternalNote] = useMutation(SaveInternalNotes);
  const { data, loading, error } = useQuery(GetUserAssessmentsNamesByCycle, {
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        userAssessmentCycleId
      }
    },
    onError: () => dispatch({ type: "error", description: "Error loading the User Assessments" })
  });

  if (loading) return <Loading />;
  if (error) return null;

  const assessmentOptions = data.userAssessments.map((assessment: UserAssessmentName) => ({
    [assessment.name]: assessment.id
  }));
  const handleInit = (): void => {
    if (noteId && noteText) {
      setNotesValue(noteText);
    } else {
      setNotesValue("");
    }
  };
  const resetAllValues = (): void => {
    setUserAssessmentValue("");
    setNotesValue("");
  };

  const handleClose = (): void => {
    dispatch({ type: "close_notes_modal" });
    resetAllValues();
  };

  const handleDetailsChange = (e: React.ChangeEvent<unknown>): void => {
    setNotesValue((e.target as HTMLInputElement).value);
  };

  const handleDropdownChange = (value: any): void => {
    setUserAssessmentValue(value);
  };

  const handleSubmit = async (): Promise<any> => {
    try {
      const adminResponse = await client.query({ query: GetAdminId, fetchPolicy: "cache-first" });
      const adminId = get(adminResponse, "data.me.id");

      if (adminId) {
        const values = {
          adminId: adminId,
          userAssessmentCycleId,
          notes: notesValue,
          userAssessmentId: userAssessmentValue || null,
          id: noteId
        };

        const response = await createInternalNote({ variables: { input: values } });
        const success = get(response, "data.saveInternalNotes.notes");
        if (success) {
          setNotesValue("");
          if (noteId) {
            dispatch({ type: "success", description: "Successfully updated note" });
          } else {
            dispatch({ type: "success", description: "Successfully created note" });
          }
          if (refetch) refetch();

          handleClose();
        } else {
          dispatch({ type: "error", description: "Failed to save note" });
        }
      } else {
        dispatch({ type: "error", description: "Failed to get Admin ID" });
      }
    } catch (error) {
      const errorMessage = get(error, "graphQLErrors[0].message", "Failed to save note");
      dispatch({ type: "error", description: errorMessage });
    }
  };
  return (
    <Modal
      aria-labelledby="schedule-coaching-session"
      aria-describedby="simple-modal-description"
      open={true}
      onClose={handleClose}
      onRendered={handleInit}
    >
      <StyledModalPaper>
        <div className="modal-title">
          <h2>Internal Notes</h2>
        </div>
        <div className="modal-content">
          <p className="assessment">Notes Category</p>
          <StyledGrid container>
            <StyledGrid item width={"40%"} marginBottom={"1rem"}>
              <SimpleDropdown
                value={userAssessmentValue}
                callback={handleDropdownChange}
                defaultText={"General"}
                options={assessmentOptions}
                fullWidth
              />
            </StyledGrid>
            <StyledGrid width={"100%"} margin={"1rem 0"}>
              <TextField
                id="outlined-multiline-static"
                fullWidth
                multiline
                placeholder="This will not be shown anywhere for the participant, this is only for internal use"
                variant="outlined"
                rows={4}
                value={notesValue}
                onChange={handleDetailsChange}
              />
            </StyledGrid>
          </StyledGrid>

          <StyledGrid container justifyContent={"center"} spacing={2} padding={"1rem 0"}>
            <StyledGrid item>
              <Button inverted onClick={handleClose}>
                Cancel
              </Button>
            </StyledGrid>
            <StyledGrid item>
              <Button onClick={handleSubmit} disabled={!notesValue}>
                Save
              </Button>
            </StyledGrid>
          </StyledGrid>
        </div>
      </StyledModalPaper>
    </Modal>
  );
};

export default NotesModal;
